import type { SupWeb3Balance, Networkish, SupWeb3TxSingleVault } from "@/types";
import React, { useMemo, useCallback } from "react";

import { SupAlert, SupXCircle } from "@repo/core-ui";
import useDeployment from "@/hooks/api/useDeployment";
import Web3Balance from "@/components/Web3/Balance";
import { notifyMessage } from "@/utils";

type Web3DepositBlockReason = "avax" | "cross-chain-multi" | "cross-chain-limit" | "chain-unsupported" | "no-contracts";

const $REASONS = {
  avax: "no cross-chain deposits allowed for AVAX at the moment",
  "cross-chain-multi": "There are 2+ groups of vaults on different chains: unsupported combination at the moment. Remove vaults from one of the chains to proceed",
  "cross-chain-limit": "No cross-chain deposits for multiple vaults allowed at the moment. Choose different chain to proceed",
  "chain-unsupported": "No deployed contracts found for the chain. Choose different chain to proceed",
  "no-contracts": "No deployed contracts found. Please try again later or contact support",
} as Record<Web3DepositBlockReason, string>;

interface Web3DepositFundingBalanceProps {
  onBalanceUpdate: (balance: SupWeb3Balance | undefined) => void;
  vaultList: SupWeb3TxSingleVault[];
}

export default function Web3DepositFundingBalance({ onBalanceUpdate, vaultList }: Web3DepositFundingBalanceProps) {
  const { data } = useDeployment();

  const { allowedChains, reason } = useMemo<{ allowedChains: Networkish[]; reason: Web3DepositBlockReason }>(() => {
    const allowedChains = data?.reduce((acc, contractConfig) => [...acc, contractConfig.ChainID], [] as Networkish[]) || [];
    return {
      allowedChains: allowedChains as Networkish[],
      reason: allowedChains.length > 0 ? "chain-unsupported" : "no-contracts",
    };
  }, [data]);

  const handleBalanceSelection = useCallback(
    (balance: SupWeb3Balance) => {
      if (allowedChains.length < 1) {
        notifyMessage("Can't select token", $REASONS[reason], "error", <SupXCircle />, undefined, 0);
      } else {
        if (allowedChains.includes(balance.chainId)) {
          onBalanceUpdate(balance);
        } else {
          notifyMessage("Choose another token", $REASONS[reason], "warning", <SupAlert />, undefined, 5000);
        }
      }
    },
    [allowedChains, reason, onBalanceUpdate]
  );

  const superPoolChains = useMemo(() => {
    if (vaultList.length > 1) {
      return [];
    }
    return vaultList.reduce<Networkish[]>((acc, vault) => {
      return vault.super_pools?.length ?? 0 > 0 ? acc.concat(vault.super_pools) : acc;
    }, []);
  }, [vaultList]);

  return (
    <Web3Balance
      showForceReload
      className="flex-grow"
      showChains
      showSearch
      strategy="default"
      hideDust={true}
      showPinnedTokens
      superPoolChains={superPoolChains}
      clickHandler={handleBalanceSelection}
    />
  );
}
